/**
 * AddToCart Component for Flash Sale
 *
 * Extends the base AddToCart component from module-quote
 * Adds gift selection workflow for flash sale products
 *
 * @module @kidslabs/module-flash-sale/modules/quote/components/add-to-cart
 */

import React from 'react';
import {
    AddToCart as BaseAddToCart,
    type AddToCartProps as BaseAddToCartProps,
    cartItemReferenceStoreAPI,
} from '@kidslabs/module-quote';
import { Fragment } from '@kidslabs/mario-nextjs-react-ui-core';
import { GiftsSelectorWidget } from '../../../gift/components/gifts-selector-widget';
import { ProductListingQuickAddGiftWidget } from '../../../gift/components/product-listing-quick-add-gift-widget';
import type { GiftsSelectorWidgetRef } from '../../../gift/components/gifts-selector-widget/gifts-selector-widget.types';
import type {
    ProductListingQuickAddGiftWidgetRef,
    ProductListingQuickAddGiftWidgetProps,
} from '../../../gift/components/product-listing-quick-add-gift-widget/product-listing-quick-add-gift-widget.types';
import { useProductGiftHook } from '../../../gift/hooks/product-gift.hook';
import { giftSelectionUtils } from '../../../gift/utils';
import { observer } from '@legendapp/state/react';

type OnFullButtonClickProps = BaseAddToCartProps['onFullButtonClick'];
type OnBuyNowClickProps = BaseAddToCartProps['onBuyNowClick'];

/**
 * Internal component that handles gift selection logic.
 * Only rendered when variant is 'full' (product detail page).
 */
const AddToCartWithGiftSelection: React.FC<BaseAddToCartProps> = observer(props => {
    const { product, variation } = props;
    const giftsSelectorRef = React.useRef<GiftsSelectorWidgetRef>(null);
    const pendingActionRef = React.useRef<(() => void | Promise<void>) | null>(null);
    const productForGift = variation?.product ?? product;

    // Observe store for reactive updates when gifts are loaded from main component
    const { sortedGiftsDataList } = useProductGiftHook({
        product: productForGift,
        observeStore: true, // Observe store instead of fetching
    });

    const handleGiftsConfirm = React.useCallback(async () => {
        if (pendingActionRef.current) {
            await pendingActionRef.current();
        }
    }, []);

    const handleGiftsClose = React.useCallback(() => {
        // Handle close if needed
    }, []);

    /**
     * Show gift selector if eligible gifts exist, otherwise execute the action directly.
     * Used for both add-to-cart and buy-now flows.
     */
    const showGiftSelectorOrExecute = React.useCallback(
        (action: () => void | Promise<void>) => {
            const currentQuantity = cartItemReferenceStoreAPI?.state?.getQuantityStepperObservable().get() ?? 1;
            const hasEligibleGifts = giftSelectionUtils.hasEligibleGiftsForQuantity(
                sortedGiftsDataList,
                currentQuantity
            );

            if (hasEligibleGifts) {
                pendingActionRef.current = action;
                giftsSelectorRef.current?.openBottomSheet();
            } else {
                action();
            }
        },
        [sortedGiftsDataList]
    );

    const onFullButtonClick: OnFullButtonClickProps = React.useCallback(
        (args: Parameters<NonNullable<OnFullButtonClickProps>>[0]) => {
            showGiftSelectorOrExecute(args.handleAddClick);
        },
        [showGiftSelectorOrExecute]
    );

    const onBuyNowClick: OnBuyNowClickProps = React.useCallback(
        (args: Parameters<NonNullable<OnBuyNowClickProps>>[0]) => {
            showGiftSelectorOrExecute(args.handleBuyNow);
        },
        [showGiftSelectorOrExecute]
    );

    return (
        <Fragment>
            {/* GiftsSelectorWidget is headless, no wrapper needed */}
            <GiftsSelectorWidget
                product={productForGift}
                ref={giftsSelectorRef}
                onConfirm={handleGiftsConfirm}
                onClose={handleGiftsClose}
            />
            <BaseAddToCart {...props} onFullButtonClick={onFullButtonClick} onBuyNowClick={onBuyNowClick} />
        </Fragment>
    );
});

/**
 * Internal component that handles PLP quick-add gift selection.
 * Uses lazy-fetch pattern: gift data is fetched ONLY when user clicks "Add to Cart".
 * This avoids N+1 GraphQL queries for each product card in the grid.
 *
 * Approach: Wraps BaseAddToCart with an invisible click interceptor.
 * When user clicks, we capture the event, open gift selection bottom sheet,
 * and on confirm, we programmatically trigger the add-to-cart action.
 */
const AddToCartWithQuickGiftSelection: React.FC<BaseAddToCartProps> = observer(props => {
    const { product } = props;
    const plpGiftRef = React.useRef<ProductListingQuickAddGiftWidgetRef>(null);
    const baseAddToCartRef = React.useRef<HTMLDivElement>(null);

    const handleGiftConfirm: ProductListingQuickAddGiftWidgetProps['onConfirm'] = React.useCallback(async params => {
        // Set the quantity from the bottom sheet stepper into the store
        // so BaseAddToCart.handleAddClick reads the correct quantity
        if (params.quantity > 0) {
            cartItemReferenceStoreAPI.actions.setQuantityStepper(params.quantity);
        }

        // After gift selection confirmed, trigger the actual add-to-cart button click
        const button = baseAddToCartRef.current?.querySelector('button');
        if (button) {
            // Create synthetic click event bypassing our interceptor
            const syntheticEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
            Object.defineProperty(syntheticEvent, '_giftConfirmed', { value: true });
            button.dispatchEvent(syntheticEvent);
        }
    }, []);

    const handleGiftClose = React.useCallback(() => {
        // No-op: session cleanup is handled by the widget
    }, []);

    const handleInterceptClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        // Skip interception for gift-confirmed clicks
        if ((event.nativeEvent as unknown as Record<string, unknown>)?._giftConfirmed) {
            return;
        }

        // Guest users CAN add to cart without authentication.
        // Authentication is only required for Buy Now / checkout actions.

        // Intercept click and open gift selection bottom sheet instead
        event.stopPropagation();
        event.preventDefault();
        plpGiftRef.current?.openBottomSheet();
    }, []);

    return (
        <Fragment>
            <ProductListingQuickAddGiftWidget
                ref={plpGiftRef}
                product={{
                    id: product?.id,
                    sku: product?.sku,
                    name: product?.name,
                    has_gift: (product as Record<string, unknown>)?.has_gift as boolean,
                    small_image: product?.small_image,
                    thumbnail: product?.thumbnail,
                }}
                onConfirm={handleGiftConfirm}
                onClose={handleGiftClose}
            />
            <div ref={baseAddToCartRef} onClickCapture={handleInterceptClick} role="presentation">
                <BaseAddToCart {...props} />
            </div>
        </Fragment>
    );
});

/**
 * AddToCart component that extends base AddToCart from module-quote
 * with gift selection capabilities for the product detail page.
 *
 * Gift logic (ProductGiftQuery, GiftsSelectorWidget) is activated
 * when variant is 'full' (mobile PDP) or 'counter' (desktop PDP).
 *
 * For 'quick' variant (product grid/PLP):
 * - ConfigurableProduct: always delegates to BaseAddToCart (navigates to PDP)
 * - Simple products with has_gift=true: uses lazy-fetch + bottom sheet for gift selection
 * - Products without gifts: delegates directly to BaseAddToCart
 */
export const AddToCart: React.FC<BaseAddToCartProps> = observer(props => {
    const { variant = 'quick', product } = props;

    // Gift selection logic is activated for 'full' (mobile PDP) and 'counter' (desktop PDP) variants.
    const shouldEnableGiftSelection = variant === 'full' || variant === 'counter';

    if (shouldEnableGiftSelection) {
        return <AddToCartWithGiftSelection {...props} />;
    }

    // For 'quick' variant: enable lazy gift selection only for simple products with gifts.
    // ConfigurableProduct should NOT enter gift flow from grid — it should navigate to PDP instead.
    const isConfigurableProduct = product?.__typename === 'ConfigurableProduct';
    const hasGift = (product as Record<string, unknown>)?.has_gift === true;

    if (variant === 'quick' && hasGift && !isConfigurableProduct) {
        return <AddToCartWithQuickGiftSelection {...props} />;
    }

    return <BaseAddToCart {...props} />;
});

AddToCart.displayName = 'AddToCart';
